import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { RegistryAsset } from '@/shared/api/transcriptor/registryApi';
import {
  CommunityTemplateDetailResponse,
  templatesApi,
} from '@/shared/api/transcriptor/templatesApi';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { buildTemplatesUrl } from '@/shared/lib';
import { toastCaller } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { registryAssetsModel } from '@/entities/registryAssets';
import { templatesModel } from '@/entities/templates';
import { resolveDefaultContentLanguage } from '@/entities/transcriptor';

import { useCommunityCatalogParams, useCommunityTemplates } from '../../lib';
import { CommunityAssetPreviewDialog } from '../CommunityAssetPreviewDialog/CommunityAssetPreviewDialog';
import { CommunityCatalogFilters } from '../CommunityCatalogFilters/CommunityCatalogFilters';
import { CommunityCatalogList } from '../CommunityCatalogList/CommunityCatalogList';

import styles from './CommunityCatalogPane.module.scss';

type CommunityCatalogPaneProps = {
  className?: string;
};

export const CommunityCatalogPane: FC<CommunityCatalogPaneProps> = (props) => {
  const { className } = props;

  const { formatMessage, locale } = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [previewAsset, setPreviewAsset] = useState<RegistryAsset | null>(null);
  const [previewDetail, setPreviewDetail] =
    useState<CommunityTemplateDetailResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isCloning, setIsCloning] = useState(false);

  const organizationId = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );

  const { params } = useCommunityCatalogParams();
  const availableLanguages = useAppSelector(
    registryAssetsModel.selectors.selectAvailableLanguages,
  );

  const language =
    params.language ??
    resolveDefaultContentLanguage({ locale, availableLanguages });

  const { items, isLoading, isLoadingMore, error, loadMore } =
    useCommunityTemplates({
      organizationId,
      params: { ...params, language },
    });

  useEffect(() => {
    if (!previewAsset) {
      return;
    }

    let cancelled = false;
    setPreviewLoading(true);
    setPreviewDetail(null);
    setPreviewError(null);

    templatesApi
      .getCommunityTemplate(previewAsset.id, organizationId)
      .then((detail) => {
        if (!cancelled) {
          setPreviewDetail(detail);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setPreviewError(err instanceof Error ? err.message : String(err));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [previewAsset, organizationId]);

  const handleClosePreview = () => {
    setPreviewAsset(null);
    setPreviewDetail(null);
    setPreviewError(null);
    setPreviewLoading(false);
    setIsCloning(false);
  };

  const navigateToLocalTemplate = (templateId: string) => {
    navigate(
      buildTemplatesUrl({ editor: 'document', mode: 'edit', id: templateId }),
    );
  };

  const adoptAsset = async (
    asset: RegistryAsset,
    targetOrganizationId: string,
  ) => {
    if (asset.originKind === 'community') {
      const { document_template_id } =
        await templatesApi.importCommunityTemplate(asset.id, {
          organization_id: targetOrganizationId,
        });

      return document_template_id;
    }

    const { local_asset_id } = await templatesApi.cloneDocumentTemplate(
      asset.id,
      {
        target_organization_id: targetOrganizationId,
      },
    );

    return local_asset_id;
  };

  const handleConfirmPreview = async () => {
    if (!previewAsset || !organizationId || isCloning) {
      return;
    }

    if (previewAsset.hasLocalClone && previewAsset.localCloneId) {
      navigateToLocalTemplate(previewAsset.localCloneId);
      handleClosePreview();
      return;
    }

    setIsCloning(true);
    try {
      const localTemplateId = await adoptAsset(previewAsset, organizationId);

      void dispatch(
        templatesModel.fetchDocumentTemplates({
          organization_id: organizationId,
        }),
      );

      toastCaller({
        type: 'success',
        message: formatMessage({
          id: 'communityCatalog.addToOrgSuccess',
          defaultMessage: 'Template added to your organization',
        }),
      });

      handleClosePreview();
      navigateToLocalTemplate(localTemplateId);
    } catch (err) {
      toastCaller({
        type: 'error',
        message:
          err instanceof Error && err.message
            ? err.message
            : formatMessage({
                id: 'communityCatalog.addToOrgError',
                defaultMessage: 'Failed to add template to organization',
              }),
      });
    } finally {
      setIsCloning(false);
    }
  };

  return (
    <div
      className={cn(styles.container, className)}
      data-section="community-catalog-pane"
    >
      <header className={styles.header}>
        <h1 className={cn('h3', styles.title)}>
          {formatMessage({
            id: 'community.pageTitle',
            defaultMessage: 'Community',
          })}
        </h1>
        <p className={cn('p3', styles.description)}>
          {formatMessage({
            id: 'community.pageDescription',
            defaultMessage:
              'Browse community encounter presets and add them to your organization.',
          })}
        </p>
      </header>

      <CommunityCatalogFilters
        availableLanguages={availableLanguages}
        language={language}
        className={styles.filters}
      />

      <CommunityCatalogList
        className={styles.list}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        items={items}
        error={error}
        onLoadMore={loadMore}
        onPreview={setPreviewAsset}
      />

      <CommunityAssetPreviewDialog
        asset={previewAsset}
        detail={previewDetail}
        error={previewError}
        isLoading={previewLoading}
        isOpen={previewAsset !== null}
        isPending={isCloning}
        onClose={handleClosePreview}
        onConfirm={handleConfirmPreview}
      />
    </div>
  );
};
